import { useState, useEffect } from "react";
import { Clock, Mail, Send, AlarmClock, X, CheckCircle, AlertCircle, Calendar, User, Bot, Play, Pause, Trash2, FileText, ExternalLink, RefreshCw, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { format } from "date-fns";
import { DeployAgentDialog } from "@/components/chaser/DeployAgentDialog";

interface ChaserData {
  id: string;
  expected_id: string;
  attempt_no: number;
  status: string;
  subject: string;
  body: string;
  recipients: string[];
  scheduled_at: string | null;
  provider?: string | null;
  message_id?: string | null;
  doc_expected: {
    period_end: string;
    due_at: string;
    status: string;
    counterparties: {
      name: string;
    };
    doc_requirements: {
      doc_type: string;
      cadence: string;
    };
  };
}

interface AgentData {
  id: string;
  name: string;
  status: string;
  config: Record<string, unknown>;
  created_at: string;
  counterparties: {
    name: string;
    type: string;
  };
}

export default function Chasers() {
  const { orgId, familyId } = useTenant();
  const [chasers, setChasers] = useState<ChaserData[]>([]);
  const [agents, setAgents] = useState<AgentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChaser, setSelectedChaser] = useState<ChaserData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [snoozeDays, setSnoozeDays] = useState("1");
  const [deployAgentDialogOpen, setDeployAgentDialogOpen] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    fetchData();
  }, [orgId, familyId]);

  const runAgentsNow = async () => {
    try {
      setRunning(true);
      const { data, error } = await supabase.functions.invoke("run-chaser-agents", {
        body: { dryRun: false },
      });

      if (error) throw error;

      const { processed, attemptsCreated, draftsCreated, emailsSent, errors } = data || {};
      const total = (draftsCreated || 0) + (emailsSent || 0);
      if (total > 0) {
        toast.success(`Chase run complete: ${draftsCreated || 0} draft(s) created, ${emailsSent || 0} email(s) sent`);
      } else if (attemptsCreated > 0) {
        toast.success(`Chase run complete: ${attemptsCreated} attempt(s) queued`);
      } else {
        toast.success(`Chase run complete: ${processed} agent(s) checked, no overdue documents`);
      }

      if (errors?.length > 0) {
        console.error("Chaser errors:", errors);
      }

      fetchData();
    } catch (err) {
      console.error("Error running chasers:", err);
      toast.error("Failed to run chasers: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setRunning(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch chasers - include draft_created for Gmail/Outlook drafts
      const { data: chaserData, error: chaserError } = await supabase
        .from("chaser_attempts")
        .select(`
          *,
          doc_expected (
            period_end,
            due_at,
            status,
            counterparties (name),
            doc_requirements (doc_type, cadence)
          )
        `)
        .eq("org_id", orgId)
        .in("status", ["proposed", "approved", "draft_created"])
        .order("scheduled_at", { ascending: true });

      if (chaserError) throw chaserError;
      setChasers(chaserData || []);

      // Fetch agents
      const { data: agentData, error: agentError } = await supabase
        .from("chaser_agents")
        .select(`
          *,
          counterparties (name, type)
        `)
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (agentError) throw agentError;
      setAgents(agentData || []);
    } catch (err) {
      toast.error("Failed to fetch data: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAndSend = async (chaserId: string) => {
    if (!confirm("Approve and send this chase email?")) return;
    
    try {
      const { error } = await supabase
        .from("chaser_attempts")
        .update({ 
          status: "sent",
          sent_at: new Date().toISOString()
        })
        .eq("id", chaserId);

      if (error) throw error;
      toast.success("Chase email marked as sent");
      fetchData();
    } catch (err) {
      toast.error("Failed to send: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleSnooze = async (chaserId: string) => {
    try {
      const days = parseInt(snoozeDays);
      const newSchedule = new Date();
      newSchedule.setDate(newSchedule.getDate() + days);

      const { error } = await supabase
        .from("chaser_attempts")
        .update({ 
          scheduled_at: newSchedule.toISOString()
        })
        .eq("id", chaserId);

      if (error) throw error;
      toast.success(`Snoozed for ${days} day(s)`);
      setSnoozeDialogOpen(false);
      setSelectedChaser(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to snooze: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleCancel = async (chaserId: string) => {
    if (!confirm("Cancel this chase?")) return;
    
    try {
      const { error } = await supabase
        .from("chaser_attempts")
        .update({ status: "cancelled" })
        .eq("id", chaserId);

      if (error) throw error;
      toast.success("Chase cancelled");
      fetchData();
    } catch (err) {
      toast.error("Failed to cancel: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleToggleAgent = async (agentId: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "paused" : "active";
    try {
      const { error } = await supabase
        .from("chaser_agents")
        .update({ status: newStatus })
        .eq("id", agentId);

      if (error) throw error;
      toast.success(`Agent ${newStatus === "active" ? "activated" : "paused"}`);
      fetchData();
    } catch (err) {
      toast.error("Failed to update agent: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!confirm("Delete this agent? This cannot be undone.")) return;
    
    try {
      const { error } = await supabase
        .from("chaser_agents")
        .delete()
        .eq("id", agentId);

      if (error) throw error;
      toast.success("Agent deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete agent: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Clock }> = {
      pending: { variant: "secondary", icon: Clock },
      late: { variant: "destructive", icon: AlertCircle },
      chasing: { variant: "default", icon: Mail },
      received: { variant: "outline", icon: CheckCircle },
      closed: { variant: "outline", icon: CheckCircle },
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant}>
        <Icon className="mr-1 h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const getAttemptStatusBadge = (chaser: ChaserData) => {
    if (chaser.status === "draft_created") {
      const providerLabel = chaser.provider === "gmail" ? "Gmail" :
                           chaser.provider === "outlook" ? "Outlook" : "Email";
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          <FileText className="mr-1 h-3 w-3" />
          Draft in {providerLabel}
        </Badge>
      );
    }
    if (chaser.status === "approved") {
      return (
        <Badge variant="default">
          <CheckCircle className="mr-1 h-3 w-3" />
          Approved
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        <Clock className="mr-1 h-3 w-3" />
        Proposed
      </Badge>
    );
  };

  const docTypeLabels: Record<string, string> = {
    quarterly_statement: "Quarterly Statement",
    k1: "K-1",
    monthly_report: "Monthly Report",
    capital_call_confirm: "Capital Call Confirmation",
    distribution_confirm: "Distribution Confirmation",
  };

  const counters = {
    activeAgents: agents.filter(a => a.status === "active").length,
    open: chasers.filter(c => c.status === "proposed").length,
    draftsReady: chasers.filter(c => c.status === "draft_created").length,
    dueToday: chasers.filter(c => {
      if (!c.scheduled_at) return false;
      const schedDate = new Date(c.scheduled_at);
      const today = new Date();
      return schedDate.toDateString() === today.toDateString() && c.status === "proposed";
    }).length,
  };

  if (loading) {
    return <div className="flex-1 p-8">Loading...</div>;
  }

  return (
    <div className="flex-1 space-y-6 px-6 py-6">
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Document Chaser Agents</h1>
          <p className="text-muted-foreground">
            Deploy agents to automatically chase missing documents from fund managers
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={runAgentsNow}
            disabled={running || agents.filter(a => a.status === "active").length === 0}
          >
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {running ? "Running..." : "Run Now"}
          </Button>
          <Button onClick={() => setDeployAgentDialogOpen(true)}>
            <Bot className="h-4 w-4" />
            Deploy Chaser Agent
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Active Agents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counters.activeAgents}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Open Chases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counters.open}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Drafts Ready</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{counters.draftsReady}</div>
            <p className="text-xs text-muted-foreground">In Gmail/Outlook</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Due Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counters.dueToday}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="queue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="queue">Chase Queue</TabsTrigger>
          <TabsTrigger value="agents">Deployed Agents</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Chase Queue</CardTitle>
              <CardDescription>
                Review proposed chase emails and approve to send
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Counterparty</TableHead>
                    <TableHead>Requirement</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attempt</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chasers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        No active chasers. All documents are up to date!
                      </TableCell>
                    </TableRow>
                  ) : (
                    chasers.map((chaser) => (
                      <TableRow key={chaser.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {chaser.doc_expected?.counterparties?.name || "Unknown"}
                          </div>
                        </TableCell>
                        <TableCell>
                          {docTypeLabels[chaser.doc_expected?.doc_requirements?.doc_type] || "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="h-3 w-3" />
                            {chaser.doc_expected?.period_end
                              ? format(new Date(chaser.doc_expected.period_end), "MMM yyyy")
                              : "—"}
                          </div>
                        </TableCell>
                        <TableCell>
                          {getAttemptStatusBadge(chaser)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">#{chaser.attempt_no}</Badge>
                        </TableCell>
                        <TableCell>
                          {chaser.status === "draft_created"
                            ? "Ready to send"
                            : chaser.scheduled_at
                              ? format(new Date(chaser.scheduled_at), "MMM d, h:mm a")
                              : "Now"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedChaser(chaser);
                                setPreviewOpen(true);
                              }}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            {chaser.status === "draft_created" ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const url = chaser.provider === "gmail"
                                      ? "https://mail.google.com/mail/u/0/#drafts"
                                      : "https://outlook.office.com/mail/drafts";
                                    window.open(url, "_blank");
                                  }}
                                >
                                  <ExternalLink className="h-4 w-4 mr-1" />
                                  Open
                                </Button>
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => handleApproveAndSend(chaser.id)}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Mark Sent
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => handleApproveAndSend(chaser.id)}
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  Send
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedChaser(chaser);
                                    setSnoozeDialogOpen(true);
                                  }}
                                >
                                  <AlarmClock className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancel(chaser.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Deployed Chaser Agents</CardTitle>
              <CardDescription>
                Agents that automatically chase fund managers for missing documents
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent Name</TableHead>
                    <TableHead>Fund Manager</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Auto-Send</TableHead>
                    <TableHead>Deployed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No agents deployed yet. Click "Deploy Chaser Agent" above to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    agents.map((agent) => (
                      <TableRow key={agent.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-muted-foreground" />
                            {agent.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          {agent.counterparties?.name || "Unknown"}
                          <div className="text-xs text-muted-foreground">
                            {agent.counterparties?.type?.toUpperCase() || "—"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={agent.status === "active" ? "default" : "secondary"}>
                            {agent.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={agent.config?.auto_send ? "destructive" : "outline"}>
                            {agent.config?.auto_send ? "Yes" : "No"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {format(new Date(agent.created_at), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleToggleAgent(agent.id, agent.status)}
                            >
                              {agent.status === "active" ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteAgent(agent.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <DeployAgentDialog 
        open={deployAgentDialogOpen}
        onOpenChange={setDeployAgentDialogOpen}
        onSuccess={fetchData} 
      />

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              Review before sending
            </DialogDescription>
          </DialogHeader>
          {selectedChaser && (
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium">To:</div>
                <div className="text-sm text-muted-foreground">
                  {selectedChaser.recipients?.join(", ") || "No recipients"}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium">Subject:</div>
                <div className="text-sm">{selectedChaser.subject || "No subject"}</div>
              </div>
              <div>
                <div className="text-sm font-medium mb-2">Body:</div>
                <div className="rounded-md border p-4 text-sm whitespace-pre-wrap bg-muted/50">
                  {selectedChaser.body || "No body content"}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={snoozeDialogOpen} onOpenChange={setSnoozeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Snooze Chase</DialogTitle>
            <DialogDescription>
              Delay this chase for a specified number of days
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Snooze for</label>
              <Select value={snoozeDays} onValueChange={setSnoozeDays}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={() => selectedChaser && handleSnooze(selectedChaser.id)} 
              className="w-full"
            >
              Snooze
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
